import { useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ChevronDown, ChevronUp, Utensils, Dumbbell, ArrowRight, BriefcaseMedical } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { DIET_TIPS, EXERCISE_TIPS, WELLNESS_UI as UI } from "@/data/wellness";
import { HEALTH_UI } from "@/data/ui/healthUi";
import { MEDKIT_UI } from "@/data/ui/medkitUi";
import { fill } from "@/lib/utils";

const PREVIEW = 4;
const TAG_LABEL = { sunnah: UI.tagSunnah, persiapan: UI.tagPrep, lansia: UI.tagSenior, medis: UI.tagMedical };
const TAG_STYLE = { sunnah: "bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))]", persiapan: "bg-primary/10 text-primary", lansia: "bg-sky-500/15 text-sky-700 dark:text-sky-300", medis: "bg-rose-500/15 text-rose-700 dark:text-rose-300" };

// One tip: icon + title (tap to expand body) + optional tag.
function Tip({ tip, section, tc }) {
  const [open, setOpen] = useState(false);
  const Icon = Icons[tip.icon] || Icons.Dot;
  return (
    <li className="rounded-xl bg-background/60 ring-1 ring-border/50" data-testid={`${section}-tip-${tip.id}`} data-open={open || undefined}>
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`${section}-tip-${tip.id}-toggle`} className="flex w-full items-center gap-3 px-3 py-2.5 text-start">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary text-primary"><Icon className="h-4 w-4" /></span>
        <span className="min-w-0 flex-1 text-sm font-medium leading-snug">{tc(tip.title)}</span>
        {tip.tag && <span className={`hidden sm:inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${TAG_STYLE[tip.tag] || ""}`}>{tc(TAG_LABEL[tip.tag])}</span>}
        {open ? <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
      </button>
      {open && <p className="px-3 pb-3 ps-14 text-xs leading-relaxed text-muted-foreground" data-testid={`${section}-tip-${tip.id}-body`}>{tc(tip.body)}</p>}
    </li>
  );
}

// Collapsible section header + list (first PREVIEW tips, then "Lihat semua").
function Section({ id, icon: Icon, title, hint, tips, accent, linkTo, linkLabel }) {
  const { tc } = useLang();
  const [open, setOpen] = useState(false);
  const [all, setAll] = useState(false);
  const shown = all ? tips : tips.slice(0, PREVIEW);
  return (
    <div className={`rounded-2xl ring-1 px-4 py-3 ${accent}`} data-testid={`wellness-${id}`} data-open={open || undefined}>
      <button onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`wellness-${id}-toggle`} className="flex w-full items-center gap-3 text-start">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-background/70"><Icon className="h-4 w-4" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{tc(title)}</span>
          <span className="block text-xs text-muted-foreground">{tc(hint)} · {tips.length}</span>
        </span>
        {open ? <ChevronUp className="h-4 w-4 shrink-0" /> : <ChevronDown className="h-4 w-4 shrink-0" />}
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <ul className="space-y-1.5" data-testid={`wellness-${id}-list`}>
            {shown.map((t) => <Tip key={t.id} tip={t} section={id} tc={tc} />)}
          </ul>
          <div className="flex flex-wrap items-center justify-between gap-2">
            {tips.length > PREVIEW && (
              <button onClick={() => setAll((a) => !a)} data-testid={`wellness-${id}-more`} className="text-xs font-medium text-primary hover:underline">
                {all ? tc(UI.showLess) : fill(tc(UI.showAll), { n: tips.length })}
              </button>
            )}
            <Link to={linkTo} data-testid={`wellness-${id}-link`} className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-medium text-white hover:opacity-90">
              {tc(linkLabel)} <ArrowRight className="h-3.5 w-3.5 rtl:-scale-x-100" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

// Balanced-diet and exercise guidance for pilgrims (11 languages), shown under the hydration reminder.
export function WellnessTips() {
  const { tc } = useLang();
  return (
    <div className="space-y-2" data-testid="wellness-tips">
      <Section id="diet" icon={Utensils} title={UI.dietTitle} hint={UI.dietHint} tips={DIET_TIPS} accent="bg-emerald-500/8 ring-emerald-500/25 text-emerald-900 dark:text-emerald-100" linkTo="/sehat?tab=menu" linkLabel={HEALTH_UI.openFullMenu} />
      <Section id="exercise" icon={Dumbbell} title={UI.exerciseTitle} hint={UI.exerciseHint} tips={EXERCISE_TIPS} accent="bg-[hsl(var(--gold))]/8 ring-[hsl(var(--gold))]/30" linkTo="/sehat?tab=program" linkLabel={HEALTH_UI.openFullProgram} />
      <Link to="/health" data-testid="wellness-medkit-link" className="flex items-center gap-3 rounded-2xl ring-1 ring-rose-500/25 bg-rose-500/8 px-4 py-3 hover:bg-rose-500/12 transition-colors">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-background/70"><BriefcaseMedical className="h-4 w-4 text-rose-600 dark:text-rose-300" /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold">{tc(MEDKIT_UI.homeLink)}</span>
          <span className="block text-xs text-muted-foreground">{tc(MEDKIT_UI.homeHint)}</span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground rtl:-scale-x-100" />
      </Link>
      <p className="px-1 text-[10px] leading-relaxed text-muted-foreground" data-testid="wellness-source">{tc(UI.source)}</p>
    </div>
  );
}
